import { useFindFirst } from "@gadgetinc/react";
import { createContext, useState, useEffect, useCallback, useMemo, lazy, Suspense } from "react";
import { api } from "../api";
import { trialCalculations } from "../utilities";
import { Banner, Text, Page } from "@shopify/polaris";
// Direct import to avoid barrel export anti-pattern
import StyledSpinner from "../components/StyledSpinner";

// Lazy load BillingPage to avoid circular dependency with providers/index.js
const BillingPage = lazy(() => import("../routes/billing"));

export const ShopContext = createContext({});

/**
 * React component that fetches shop data
 * Key features:
 *  - Fetch shop
 *  - Gate usage of the app if trial is over and merchant hasn't paid
 */
export default ({ children }) => {
  const [show, setShow] = useState(false);
  const [bannerContext, setBannerContext] = useState("");

  // Live queries ensure subscriptionStatus updates from Shopify webhooks are reflected in real-time
  const [{ data: shop, fetching: fetchingShop, error: errorFetchingShop }] =
    useFindFirst(api.shopifyShop, {
      select: {
        id: true,
        currency: true,
        moneyFormat: true,
        moneyWithCurrencyFormat: true,
        usedTrialMinutes: true,
        trialStartedAt: true,
        trialDays: true,
        currentPlan: true,
        subscriptionStatus: true,
        myshopifyDomain: true,  // For theme extension deep linking
      },
      live: true,
    });

  // Compute trial days synchronously during render (avoids extra re-render from useEffect+setState)
  const availableTrialDays = useMemo(() => {
    if (!shop) return 0;
    return trialCalculations(
      shop.usedTrialMinutes,
      shop.trialStartedAt,
      new Date(),
      shop.trialDays
    ).availableTrialDays;
  }, [shop]);

  const handleDismiss = useCallback(() => {
    setShow(false);
  }, []);

  // useEffect for showing a banner if there's and error fetching shop information
  useEffect(() => {
    if (!fetchingShop && errorFetchingShop) {
      setBannerContext(errorFetchingShop.message);
      setShow(true);
    } else if (fetchingShop) {
      setShow(false);
    }
  }, [fetchingShop, errorFetchingShop]);

  if (fetchingShop || !shop) {
    return <StyledSpinner />;
  }

  return (
    <ShopContext.Provider
      value={{
        shop,
      }}
    >
      {show && (
        <Page>
          <Banner
            title={bannerContext}
            tone="critical"
            onDismiss={handleDismiss}
          />
        </Page>
      )}
      {!availableTrialDays && shop?.subscriptionStatus !== 'active' ? (
        <Suspense fallback={<StyledSpinner />}>
          <BillingPage />
        </Suspense>
      ) : (
        <>
          {/* {!!availableTrialDays && (
            <Page>
              <Banner
                title="Welcome to the app! You are currently on a trial period."
                tone="info"
              >
                <Text as="p" variant="bodyMd">
                  The trial will end in <strong>{availableTrialDays}</strong>{" "}
                  days.
                </Text>
              </Banner>
            </Page>
          )} */}
          {children}
        </>
      )}
    </ShopContext.Provider>
  );
};
